import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Grid,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import {
  Description,
  Camera,
  Schedule,
  Settings,
  PhotoLibrary,
  People,
  Campaign,
  ArrowForward,
  CheckCircle,
  Circle
} from '@mui/icons-material';

interface ResourceOverviewProps {
  resource: any;
  onPhaseJump: (phaseIndex: number) => void;
  onUpdate: (updatedResource: any) => void;
}

const ResourceOverview: React.FC<ResourceOverviewProps> = ({ 
  resource, 
  onPhaseJump, 
  onUpdate 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Analyze resource content for status display
  const getContentAnalysis = () => {
    const analysis = {
      technical: {
        hasContent: false,
        items: [] as string[],
        icon: Settings
      },
      marketing: {
        hasContent: false,
        items: [] as string[],
        icon: Camera
      },
      schedule: {
        hasContent: false,
        items: [] as string[],
        icon: Schedule
      },
      invites: {
        hasContent: false,
        items: [] as string[],
        icon: People
      },
      campaign: {
        hasContent: false,
        items: [] as string[],
        icon: Campaign
      }
    };

    // Technical Analysis
    if (resource.schedulePdfUrl) analysis.technical.items.push('Schedule PDF');
    if (resource.signedContractUrl) analysis.technical.items.push('Signed Contract');
    if (resource.techSpecs) analysis.technical.items.push('Tech Specs');
    if (resource.setupNotes) analysis.technical.items.push('Setup Notes');
    analysis.technical.hasContent = analysis.technical.items.length > 0;

    // Marketing Analysis - Check all possible image locations
    let marketingImageCount = parseInt(resource.metaSummary?.promoImageCount || '0');
    
    // Fallback: Check actual data in all possible locations
    if (marketingImageCount === 0) {
      const actualImageCount = 
        // Check additionalPromoImages (old location)
        (resource.additionalPromoImages?.filter((img: any) => img.url)?.length || 0) + 
        // Check marketingAssets for images (new location)
        (resource.marketingAssets?.filter((asset: any) => asset.type === 'image' && asset.url)?.length || 0) +
        // Check mainImage and mainPosterImage
        (resource.mainImage?.url ? 1 : 0) + 
        (resource.mainPosterImage?.url ? 1 : 0);
        
      if (actualImageCount > 0) {
        marketingImageCount = actualImageCount;
      }
    }
    
    if (marketingImageCount > 0) analysis.marketing.items.push(`${marketingImageCount} images`);
    if (resource.pressRelease || resource.pressReleaseUrl) analysis.marketing.items.push('Press release');
    if (resource.marketingEmailContent) analysis.marketing.items.push('Email content');
    if (resource.socialMediaContent?.length > 0) analysis.marketing.items.push('Social content');
    analysis.marketing.hasContent = analysis.marketing.items.length > 0;

    // Marketing Schedule Analysis
    const scheduleItems = resource.marketingSchedule?.length || 0;
    const completedTasks = resource.marketingSchedule?.filter((task: any) => task.status === 'completed').length || 0;
    
    if (scheduleItems > 0) {
      analysis.schedule.items.push(`${scheduleItems} tasks`);
      if (completedTasks > 0) analysis.schedule.items.push(`${completedTasks} completed`);
    }
    analysis.schedule.hasContent = scheduleItems > 0;

    // Invites Analysis
    const totalInvites = resource.targetedInvites?.length || 0;
    const sentInvites = resource.targetedInvites?.filter((inv: any) => inv.invitationStatus !== 'draft').length || 0;
    const confirmedInvites = resource.targetedInvites?.filter((inv: any) => inv.invitationStatus === 'confirmed_yes').length || 0;
    
    if (totalInvites > 0) analysis.invites.items.push(`${totalInvites} contacts`);
    if (sentInvites > 0) analysis.invites.items.push(`${sentInvites} sent`);
    if (confirmedInvites > 0) analysis.invites.items.push(`${confirmedInvites} confirmed`);
    if (resource.inviteEmailTemplate) analysis.invites.items.push('Email template');
    analysis.invites.hasContent = totalInvites > 0;

    // Campaign Analysis
    const timelineItems = resource.publicityTimeline?.length || 0;
    if (timelineItems > 0) analysis.campaign.items.push(`${timelineItems} timeline items`);
    analysis.campaign.hasContent = timelineItems > 0;

    return analysis;
  };

  const analysis = getContentAnalysis();

  // Determine which sections to show based on resource type
  const getRelevantSections = () => {
    const sections = ['technical']; // Always show technical
    
    if (resource.resourceType === 'full') {
      return ['technical', 'marketing', 'schedule', 'invites', 'campaign'];
    } else if (resource.resourceType === 'marketing') {
      return ['technical', 'marketing', 'schedule'];
    } else if (resource.resourceType === 'invites') {
      return ['technical', 'invites'];
    } else {
      return ['technical'];
    }
  };

  const relevantSections = getRelevantSections();

  // Get next steps suggestions (lighter touch)
  const getNextSteps = () => {
    const steps = [];
    
    if (!resource.signedContractUrl) {
      steps.push({
        label: 'Upload contract when ready',
        phase: 1,
        icon: Description
      });
    }

    if (resource.resourceType === 'marketing' || resource.resourceType === 'full') {
      if (!analysis.marketing.hasContent) {
        steps.push({
          label: 'Add marketing materials',
          phase: 2,
          icon: PhotoLibrary
        });
      } else if (!analysis.schedule.hasContent) {
        steps.push({
          label: 'Create promotional schedule',
          phase: 3,
          icon: Schedule
        });
      }
    }

    if (resource.resourceType === 'invites' || resource.resourceType === 'full') {
      if (!analysis.invites.hasContent) {
        steps.push({
          label: 'Set up invitation list',
          phase: 4,
          icon: People
        });
      }
    }

    return steps.slice(0, 2); // Keep it simple with max 2 suggestions
  };

  const nextSteps = getNextSteps();

  return (
    <Box sx={{ mb: 3 }}>
      {/* Content Overview Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}>
            <Description sx={{ mr: 1 }} />
            Content Overview
          </Typography>
          
          <Grid container spacing={2}>
            {relevantSections.map((section) => {
              const sectionData = analysis[section as keyof typeof analysis];
              
              return (
                <Grid item xs={12} sm={6} lg={relevantSections.length > 3 ? 2.4 : 3} key={section}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: '1px solid',
                      borderColor: sectionData.hasContent ? 'success.main' : 'divider',
                      backgroundColor: sectionData.hasContent ? 'success.50' : 'background.paper',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: sectionData.hasContent ? 'success.100' : 'action.hover',
                        transform: 'translateY(-1px)'
                      }
                    }}
                    onClick={() => {
                      // Jump to the phase
                      if (section === 'technical') onPhaseJump(1);
                      else if (section === 'marketing') onPhaseJump(2);
                      else if (section === 'schedule') onPhaseJump(3);
                      else if (section === 'invites') onPhaseJump(4);
                      else if (section === 'campaign') onPhaseJump(5);
                      
                      // Scroll to the tab section after a brief delay to let the tab change
                      setTimeout(() => {
                        const tabsElement = document.querySelector('[role="tablist"]');
                        if (tabsElement) {
                          tabsElement.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'start' 
                          });
                        }
                      }, 100);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {sectionData.hasContent ? (
                        <CheckCircle color="success" sx={{ mr: 1 }} />
                      ) : (
                        <Circle color="action" sx={{ mr: 1 }} />
                      )}
                      <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', fontWeight: 500, color: 'primary.main' }}>
                        {section === 'schedule' ? 'Marketing Schedule' : section}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.primary" sx={{ mb: 1, minHeight: 40 }}>
                      {sectionData.items.length > 0 ? sectionData.items.join(', ') : 'Click to get started'}
                    </Typography>
                    
                    <Typography variant="caption" color={sectionData.hasContent ? 'success.dark' : 'primary.main'} sx={{ fontWeight: 500 }}>
                      {sectionData.hasContent ? 'Has content' : 'Go to section →'}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Next Steps Section - Only show if there are suggestions */}
      {nextSteps.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
              Suggested Next Steps
            </Typography>
            
            <Stack spacing={2}>
              {nextSteps.map((step, index) => {
                const StepIcon = step.icon;
                return (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper'
                    }}
                  >
                    <StepIcon color="primary" sx={{ mr: 2 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {step.label}
                      </Typography>
                    </Box>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => {
                        onPhaseJump(step.phase);
                        // Scroll to tabs after jumping to phase
                        setTimeout(() => {
                          const tabsElement = document.querySelector('[role="tablist"]');
                          if (tabsElement) {
                            tabsElement.scrollIntoView({ 
                              behavior: 'smooth', 
                              block: 'start' 
                            });
                          }
                        }, 100);
                      }}
                      endIcon={<ArrowForward />}
                    >
                      Go
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ResourceOverview;